import mixpanel from "mixpanel-browser";

// Initialize Mixpanel with the token from environment variables
const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_KEY;
const IS_DEV = import.meta.env.DEV;

let isInitialized = false;

/**
 * Initialize Mixpanel with autocapture enabled.
 * Should be called once when the app starts.
 *
 * Autocapture automatically tracks:
 * - Page views
 * - Clicks (buttons, links, etc.)
 * - Form submissions
 * - Input changes
 */
export function initMixpanel() {
  if (isInitialized) return;

  // Skip analytics in development
  if (IS_DEV) {
    return;
  }

  if (!MIXPANEL_TOKEN) {
    console.warn(
      "VITE_MIXPANEL_KEY is not defined. Analytics will be disabled.",
    );
    return;
  }

  mixpanel.init(MIXPANEL_TOKEN, {
    track_pageview: "url-with-path", // Automatically track page views
    persistence: "localStorage",
    ignore_dnt: false, // Respect Do Not Track
    autocapture: {
      capture_extra_attrs: ["data-mp-element-name", "data-mp-cta-location"],
    },
    record_sessions_percent: 1, // Session Replay enabled, recording 1% of all sessions
    record_heatmap_data: true, // Enable Heatmap data collection
  });

  isInitialized = true;
}

/**
 * Check if Mixpanel is properly initialized
 */
export function isMixpanelReady(): boolean {
  return !IS_DEV && isInitialized && !!MIXPANEL_TOKEN;
}

/**
 * Track a custom event (for events not covered by autocapture).
 *
 * Naming convention: new event names and property keys use snake_case, per
 * Mixpanel's recommended convention (https://help.mixpanel.com/hc/en-us/articles/360033248932).
 * Some older events in this codebase use Title Case with spaces (e.g.
 * `'Event Viewed'`, `'Account Created'`) — leave those as-is to avoid
 * breaking existing dashboards, but do not add new events in that style.
 */
export function trackEvent(
  eventName: string,
  properties?: Record<string, unknown>,
) {
  if (!isMixpanelReady()) return;

  mixpanel.track(eventName, properties);
}

/**
 * Identify a user (call when user signs up or logs in)
 */
export function identifyUser(userId: string, traits?: Record<string, unknown>) {
  if (!isMixpanelReady()) return;

  mixpanel.identify(userId);

  if (traits) {
    mixpanel.people.set(traits);
  }
}

/**
 * Set user properties without identifying
 */
export function setUserProperties(properties: Record<string, unknown>) {
  if (!isMixpanelReady()) return;

  mixpanel.people.set(properties);
}

/**
 * Reset/logout the current user
 */
export function resetUser() {
  if (!isMixpanelReady()) return;

  mixpanel.reset();
}

/**
 * Register super properties (sent with every event)
 */
export function registerSuperProperties(properties: Record<string, unknown>) {
  if (!isMixpanelReady()) return;

  mixpanel.register(properties);
}

// Export the mixpanel instance for advanced usage
export { mixpanel };
